import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AttendanceMonthBody, AttendanceRecord, ParentAttendanceService, PeriodAttendance } from './parent-attendance.service';

interface CalendarDay {
  date: Date;
  inMonth: boolean;
  isFuture: boolean;
  dateKey: string;
  status: string | null;
}

// Attendance — monthly calendar (AttendanceCalendar.js). Prev/Next month
// buttons take the place of the mobile app's swipe gesture. Rendered
// conditionally inside ParentLoginComponent's dashboard (no routing), same
// pattern as ParentDiaryComponent.
@Component({
  selector: 'app-parent-attendance',
  templateUrl: './parent-attendance.component.html',
  styleUrls: ['./parent-attendance.component.css'],
})
export class ParentAttendanceComponent implements OnInit, OnChanges {

  @Input() studentId = '';
  @Input() classId = '';
  @Input() schoolId = '';
  @Input() academicId = '';

  monthCursor = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  loading = false;
  error = '';
  private loadRequestId = 0;
  attendancePercentage = 0;
  attendanceType: 'PERIOD' | 'DAY' = 'DAY';
  private attendanceByDate = new Map<string, string>();

  calendarDays: CalendarDay[] = [];
  weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  selectedDate: Date | null = null;
  selectedDateKey: string | null = null;
  periodDetails: PeriodAttendance[] = [];
  periodLoading = false;
  periodError = '';

  constructor(private attendanceService: ParentAttendanceService) { }

  ngOnInit(): void {
    if (this.studentId && this.classId) {
      this.loadMonth();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['studentId'] || changes['classId']) && this.studentId && this.classId) {
      this.selectedDate = null;
      this.selectedDateKey = null;
      this.periodDetails = [];
      this.loadMonth();
    }
  }

  private get lang(): string {
    // Deliberately always 'en', not the UI's selected display language —
    // the parent mobile app's backend endpoints expect this constant
    // regardless of what language the user has the interface in; switching
    // the UI language must never change this API payload field.
    return 'en';
  }

  private dateKey(d: Date): string {
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // The API reference shows CreatedDate as a plain "YYYY-MM-DD" string, but
  // real backend date fields in this codebase often come back as full ISO
  // datetimes ("2026-09-01T00:00:00.000Z") or "DD/MM/YYYY" — normalize
  // whatever shape arrives to the same "YYYY-MM-DD" key buildCalendar()
  // looks up, so a format mismatch doesn't silently leave every tile
  // unmapped (grey).
  private normalizeDateKey(raw: string): string {
    if (!raw) { return ''; }
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) { return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`; }
    const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashMatch) {
      return `${slashMatch[3]}-${slashMatch[2].padStart(2, '0')}-${slashMatch[1].padStart(2, '0')}`;
    }
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? raw : this.dateKey(parsed);
  }

  get monthLabel(): string {
    return this.monthCursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }

  prevMonth(): void {
    this.monthCursor = new Date(this.monthCursor.getFullYear(), this.monthCursor.getMonth() - 1, 1);
    this.selectedDate = null;
    this.selectedDateKey = null;
    this.periodDetails = [];
    this.loadMonth();
  }

  nextMonth(): void {
    this.monthCursor = new Date(this.monthCursor.getFullYear(), this.monthCursor.getMonth() + 1, 1);
    this.selectedDate = null;
    this.selectedDateKey = null;
    this.periodDetails = [];
    this.loadMonth();
  }

  loadMonth(): void {
    this.loading = true;
    this.error = '';
    const attendanceDate = `${`${this.monthCursor.getMonth() + 1}`.padStart(2, '0')}/${this.monthCursor.getFullYear()}`;
    // Bumped on every call so a still-in-flight request for the PREVIOUS
    // student can't land after the new student's request and overwrite its
    // fresher data with stale data.
    const requestId = ++this.loadRequestId;
    this.attendanceService.getMonthlyAttendance(this.studentId, this.classId, this.schoolId, this.lang, attendanceDate, this.academicId)
      .subscribe({
        next: (res: any) => {
          if (requestId !== this.loadRequestId) { return; }
          this.loading = false;
          if (res && res.status && res.body) {
            const body: AttendanceMonthBody = res.body;
            this.attendancePercentage = body.attendancePercentage || 0;
            this.attendanceType = body.attendanceType || 'DAY';
            this.attendanceByDate = new Map();
            (body.attendances || []).forEach((rec: AttendanceRecord) => {
              this.attendanceByDate.set(this.normalizeDateKey(rec.CreatedDate), rec.AttendanceStatus);
            });
          } else {
            this.attendanceByDate = new Map();
            this.attendancePercentage = 0;
          }
          this.buildCalendar();
        },
        error: () => {
          if (requestId !== this.loadRequestId) { return; }
          this.loading = false;
          this.error = 'Unable to load attendance.';
          this.attendanceByDate = new Map();
          this.buildCalendar();
        },
      });
  }

  private buildCalendar(): void {
    const year = this.monthCursor.getFullYear();
    const month = this.monthCursor.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(year, month, 1 - startOffset);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days: CalendarDay[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      const key = this.dateKey(d);
      days.push({
        date: d,
        inMonth: d.getMonth() === month,
        isFuture: d.getTime() > today.getTime(),
        dateKey: key,
        status: this.attendanceByDate.get(key) || null,
      });
    }
    this.calendarDays = days;
  }

  onDayClick(day: CalendarDay): void {
    if (!day.inMonth || day.isFuture || day.status !== 'PARTIAL') { return; }
    this.selectedDate = day.date;
    this.selectedDateKey = day.dateKey;
    this.periodLoading = true;
    this.periodError = '';
    this.periodDetails = [];
    const attendanceDate = `${day.date.getFullYear()}${`${day.date.getMonth() + 1}`.padStart(2, '0')}${`${day.date.getDate()}`.padStart(2, '0')}`;
    this.attendanceService.getPeriodWiseAttendance(this.studentId, this.classId, this.lang, attendanceDate, this.academicId)
      .subscribe({
        next: (res: any) => {
          this.periodLoading = false;
          this.periodDetails = Array.isArray(res?.body) ? res.body : [];
        },
        error: () => {
          this.periodLoading = false;
          this.periodError = 'Unable to load period details.';
        },
      });
  }

  closePeriodDetails(): void {
    this.selectedDate = null;
    this.selectedDateKey = null;
    this.periodDetails = [];
  }

  formatPeriodTime(time: string): string {
    if (!time) { return ''; }
    const [hStr, mStr] = time.split(':');
    let h = parseInt(hStr, 10);
    const suffix = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${mStr} ${suffix}`;
  }
}
